/**
 * Currencies that default to no decimals according to Shopify documentation
 */
const NO_DECIMAL_CURRENCIES = ['BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW', 'PYG', 'RWF', 'UGX', 'UYI', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'];
/**
 * Checks if a currency code should use no decimals by default
 * @param currencyCode - The 3-letter currency code
 */
const shouldUseNoDecimals = (currencyCode) => {
    if (!currencyCode)
        return false;
    return NO_DECIMAL_CURRENCIES.includes(currencyCode.toUpperCase());
};
/**
 * Formats money according to Shopify currency formatting standards
 * Supports all currency formatting options from Shopify documentation:
 *
 * - amount: 1,134.65 (default format with comma thousands separator and period decimal)
 * - amount_no_decimals: 1,135 (no decimals, rounded according to Shopify rules)
 * - amount_with_comma_separator: 1.134,65 (period thousands separator, comma decimal)
 * - amount_no_decimals_with_comma_separator: 1.135 (no decimals with period thousands separator)
 * - amount_with_apostrophe_separator: 1'134.65 (apostrophe thousands separator)
 * - amount_no_decimals_with_space_separator: 1 135 (space thousands separator, no decimals)
 * - amount_with_space_separator: 1 134,65 (space thousands separator, comma decimal)
 * - amount_with_period_and_space_separator: 1 134.65 (space thousands separator, period decimal)
 *
 * Rounding rules: If decimal >= 50, round up; if < 50, round down (for no-decimal formats)
 *  * @param cents - The price in cents (number or string)
 * @param format - Currency format string (e.g., "${{amount}}", "{{amount}} Kr")
 * @param showSign - Whether to show +/- signs
 * @param showSymbol - Whether to include currency symbol
 * @param symbolOnly - Return only the currency symbol
 * @param currencyCode - Optional 3-letter currency code (affects decimal handling)
 */
export const FormatMoney = (cents, format = '${{amount}}', showSign = false, showSymbol = true, symbolOnly = false, currencyCode) => {
    var _a, _b;
    format = format || '${{amount}}';
    // If symbolOnly is true, extract and return only the currency symbol
    if (symbolOnly) {
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        // Check if symbol is before placeholder (like ${{amount}})
        const beforeMatch = format.match(/(.+?)\{\{/);
        const beforeSymbol = beforeMatch && beforeMatch[1] ? beforeMatch[1].trim() : '';
        // Check if symbol is after placeholder (like {{amount}} Kr)
        const afterMatch = format.match(/\}\}(.+)/);
        const afterSymbol = afterMatch && afterMatch[1] ? afterMatch[1].trim() : '';
        // Return combined symbols or empty string if none found
        return beforeSymbol + afterSymbol || '';
    }
    if (typeof cents == 'string') {
        cents = Number(cents.replace('.', ''));
    }
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '${{amount}}';
    const isNegative = cents < 0;
    const absValue = Math.abs(Number(cents));
    function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = precision !== null && precision !== void 0 ? precision : 2;
        if (isNaN(number) || number == null) {
            return '0';
        }
        // Apply Shopify rounding rules for no-decimal formats
        let adjustedNumber = number / 100.0;
        if (precision === 0) {
            const fractionalPart = (adjustedNumber % 1) * 100;
            if (fractionalPart >= 50) {
                adjustedNumber = Math.ceil(adjustedNumber);
            }
            else {
                adjustedNumber = Math.floor(adjustedNumber);
            }
        }
        const numberWithDecimals = adjustedNumber.toFixed(precision);
        // Set default separators
        thousands = thousands !== null && thousands !== void 0 ? thousands : ',';
        decimal = decimal !== null && decimal !== void 0 ? decimal : '.';
        const parts = numberWithDecimals.split('.'), dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands), cents = parts[1] ? decimal + parts[1] : '';
        return dollars + cents;
    }
    const formatType = (_b = (_a = formatString.match(placeholderRegex)) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : 'amount';
    // Check if currency should default to no decimals
    const useNoDecimals = shouldUseNoDecimals(currencyCode);
    switch (formatType) {
        case 'amount':
            // Default format: 1,134.65 (or no decimals for certain currencies)
            value = formatWithDelimiters(absValue, useNoDecimals ? 0 : 2);
            break;
        case 'amount_no_decimals':
            // No decimals: 1,135
            value = formatWithDelimiters(absValue, 0);
            break;
        case 'amount_with_comma_separator':
            // Comma as decimal separator: 1.134,65 (or no decimals for certain currencies)
            value = formatWithDelimiters(absValue, useNoDecimals ? 0 : 2, '.', ',');
            break;
        case 'amount_no_decimals_with_comma_separator':
            // No decimals with comma separator: 1.135
            value = formatWithDelimiters(absValue, 0, '.', ',');
            break;
        case 'amount_with_apostrophe_separator':
            // Apostrophe as thousands separator: 1'134.65 (or no decimals for certain currencies)
            value = formatWithDelimiters(absValue, useNoDecimals ? 0 : 2, "'", '.');
            break;
        case 'amount_no_decimals_with_space_separator':
            // No decimals with space separator: 1 135
            value = formatWithDelimiters(absValue, 0, ' ', '.');
            break;
        case 'amount_with_space_separator':
            // Space separator with comma decimal: 1 134,65 (or no decimals for certain currencies)
            value = formatWithDelimiters(absValue, useNoDecimals ? 0 : 2, ' ', ',');
            break;
        case 'amount_with_period_and_space_separator':
            // Space separator with period decimal: 1 134.65 (or no decimals for certain currencies)
            value = formatWithDelimiters(absValue, useNoDecimals ? 0 : 2, ' ', '.');
            break;
    }
    // Apply sign if showSign is true
    if (showSign) {
        if (isNegative) {
            value = '-' + value;
        }
        else {
            value = '+' + value;
        }
    }
    else if (isNegative) {
        // Always show negative sign even if showSign is false
        value = '-' + value;
    }
    // Handle currency symbol display
    if (showSymbol) {
        return formatString.replace(placeholderRegex, value.toString());
    }
    else {
        // Extract the placeholder pattern and replace the entire format with just the value
        const match = formatString.match(placeholderRegex);
        if (match && match.index !== undefined) {
            // Just return the formatted value without the surrounding currency format
            return value.toString();
        }
        // Fallback if no placeholder found
        return formatString.replace(placeholderRegex, value.toString());
    }
};
